import { useCallback, useEffect, useRef } from 'react';
import NeshanMap from '@neshan-maps-platform/react-openlayers';
import '@neshan-maps-platform/react-openlayers/dist/style.css';
import Feature from '@neshan-maps-platform/ol/Feature';
import Point from '@neshan-maps-platform/ol/geom/Point';
import Style from '@neshan-maps-platform/ol/style/Style';
import Icon from '@neshan-maps-platform/ol/style/Icon';
import VectorSource from '@neshan-maps-platform/ol/source/Vector';
import VectorLayer from '@neshan-maps-platform/ol/layer/Vector';
import Translate from '@neshan-maps-platform/ol/interaction/Translate';
import { fromLonLat, toLonLat } from '@neshan-maps-platform/ol/proj';

const IRAN_CENTER = { lat: 32.4279, lng: 53.688 };

// Inline SVG pin: the SDK ships no marker asset and the old
// static.neshan.org marker URL 404s, which left the Icon with no image.
const PIN_SRC = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(
  '<svg xmlns="http://www.w3.org/2000/svg" width="34" height="44" viewBox="0 0 34 44">' +
    '<path d="M17 1C8.7 1 2 7.7 2 16c0 10.6 13.5 25.4 14.1 26.1.5.5 1.3.5 1.8 0C18.5 41.4 32 26.6 32 16C32 7.7 25.3 1 17 1z" ' +
    'fill="#e53935" stroke="#fff" stroke-width="2"/>' +
    '<circle cx="17" cy="16" r="6" fill="#fff"/></svg>',
)}`;
const PIN_STYLE = new Style({
  image: new Icon({ src: PIN_SRC, anchor: [0.5, 1], width: 34, height: 44 }),
});

// Above Neshan's raster/POI layers so the pin is never covered.
const PIN_Z_INDEX = 999;

const coordKey = (lat, lng) => `${lat},${lng}`;

/**
 * Neshan map with a draggable pin for picking an exact location.
 *
 * Usage:
 *   <LocationMapPicker
 *     center={cityCenter}      // { lat, lng } from the selected city, or null
 *     value={pin}              // { lat, lng } | null — current pin position
 *     onChange={(coords) => setPin(coords)}
 *   />
 */
export default function LocationMapPicker({
  center,
  zoom = 14,
  value,
  onChange,
  height = 320,
}) {
  const mapRef = useRef(null);
  const layerRef = useRef(null);
  const featureRef = useRef(null);
  const interactionRef = useRef(null);
  // The last position this component emitted via click/drag. A `value` that
  // matches it came from our own pointer event, so the map should not recenter;
  // anything else came from outside (form load, saved location) and should.
  const lastEmittedRef = useRef(null);
  const onChangeRef = useRef(onChange);
  useEffect(() => {
    onChangeRef.current = onChange;
  });

  const mapCenter = center ?? IRAN_CENTER;
  const initialZoom = center ? zoom : 5;

  // Move (or drop) the pin feature immediately, without rebuilding the layer.
  const placePin = useCallback((lat, lng) => {
    const layer = layerRef.current;
    if (!layer) return;

    const coords = fromLonLat([lng, lat]);
    let feature = featureRef.current;
    if (!feature) {
      feature = new Feature({ geometry: new Point(coords) });
      feature.setStyle(PIN_STYLE);
      layer.getSource().addFeature(feature);
      featureRef.current = feature;
    } else {
      feature.getGeometry().setCoordinates(coords);
    }
  }, []);

  // The layer, source and drag interaction are created once and reused; the
  // pin itself is updated in place so it is never lost or duplicated.
  useEffect(() => {
    const map = mapRef.current?.map;
    if (!map) return;

    if (!layerRef.current) {
      const layer = new VectorLayer({
        source: new VectorSource(),
        zIndex: PIN_Z_INDEX,
      });
      map.addLayer(layer);
      layerRef.current = layer;

      // Default hitTolerance is 0px, and the feature's anchor sits at the
      // icon's sharp bottom tip (anchor [0.5,1]) — a near-zero-width,
      // anti-aliased point. With no tolerance, dragging requires clicking
      // that exact pixel, so grabbing the visibly large pin body fails.
      const translate = new Translate({ layers: [layer], hitTolerance: 20 });
      translate.on('translateend', (event) => {
        const feature = event.features.getArray()[0];
        if (!feature) return;
        const [lng, lat] = toLonLat(feature.getGeometry().getCoordinates());
        lastEmittedRef.current = coordKey(lat, lng);
        onChangeRef.current?.({ lat, lng });
      });
      map.addInteraction(translate);
      interactionRef.current = translate;
    }

    if (value) {
      placePin(value.lat, value.lng);
      if (coordKey(value.lat, value.lng) !== lastEmittedRef.current) {
        map.getView().animate({
          center: fromLonLat([value.lng, value.lat]),
          zoom,
          duration: 600,
        });
      }
    } else if (featureRef.current) {
      layerRef.current.getSource().removeFeature(featureRef.current);
      featureRef.current = null;
    }
  }, [value, zoom, placePin]);

  // `center` is initial-only on NeshanMap, so re-center imperatively
  // whenever the selected city changes after mount.
  useEffect(() => {
    const map = mapRef.current?.map;
    if (!map || !center) return;
    map.getView().animate({
      center: fromLonLat([center.lng, center.lat]),
      zoom,
      duration: 1000,
    });
  }, [center?.lat, center?.lng, zoom]);

  // Map is created once by the SDK and outlives this component's effects, so
  // tear the layer + interaction down only on real unmount.
  useEffect(() => {
    return () => {
      const map = mapRef.current?.map;
      if (interactionRef.current) {
        map?.removeInteraction(interactionRef.current);
        interactionRef.current = null;
      }
      if (layerRef.current) {
        map?.removeLayer(layerRef.current);
        layerRef.current = null;
      }
      featureRef.current = null;
    };
  }, []);

  const handleInit = (map) => {
    mapRef.current = { map };

    // Click anywhere on the map to drop/move the pin.
    map.on('click', (event) => {
      const [lng, lat] = toLonLat(event.coordinate);
      placePin(lat, lng);
      lastEmittedRef.current = coordKey(lat, lng);
      onChangeRef.current?.({ lat, lng });
    });
  };

  return (
    <div
      className="location-map-picker"
      style={{
        "--location-map-height":
          typeof height === "number" ? `${height}px` : height,
      }}
    >
      <NeshanMap
        mapKey={import.meta.env.VITE_NESHAN_MAP_KEY ?? ''}
        center={{ latitude: mapCenter.lat, longitude: mapCenter.lng }}
        zoom={initialZoom}
        onInit={handleInit}
        style={{ height: '100%', width: '100%' }}
      />
    </div>
  );
}
